import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useActorMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import isFuture from 'date-fns/isFuture'
import { isEmpty, isNil } from 'ramda'
import { useState } from 'react'
import { useTheme } from 'styled-components'

import { BottomSheet } from '@/@bothrs-ui/web/components/bottom-sheet/BottomSheet'
import { Flex, FlexColumn, FlexRow, Margin, Padding } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { formatPhoneNumber } from '@/utils/format-phone-number'
import {
  AdditionalContactCard,
  AdditionalContactCardAction,
  AdditionalContactCardContent,
} from '../../../onboarding/pages/additional-contact-information/components/AddContactsBox.styled'
import { ListItem } from './AddContactsListItem'

import type { AdditionalContact } from '@/modules/onboarding/pages/additional-contact-information/components/AddContactsBox'

type Props = {
  contacts: AdditionalContact[]
  kinshipTypes?: { id: string; value: string; label: string }[]
  onAddContact: () => void
  onEditContact: (contact: AdditionalContact) => void
  onReorderContacts?: (contacts: AdditionalContact[]) => void
  onRemoveContact: (healthblocksId: string) => void
}

export const AddContactsList = ({
  contacts,
  kinshipTypes,
  onAddContact,
  onEditContact,
  onReorderContacts,
  onRemoveContact,
}: Props) => {
  const theme = useTheme()
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const actorMember = useActorMember()

  const visibleContacts = contacts.filter((c) => (c.endDate ? isFuture(Date.parse(c.endDate)) : true))

  const deletedContacts = contacts.filter((c) => !(c.endDate ? isFuture(Date.parse(c.endDate)) : true))

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function handleReorder(originalIndex: number, targetIndex: number) {
    const newArray = [...visibleContacts]

    const item = newArray.splice(originalIndex, 1)[0]
    newArray.splice(targetIndex, 0, item)

    if (onReorderContacts) {
      onReorderContacts([...newArray.map((value, index) => ({ ...value, order: index })), ...deletedContacts])
    }
  }

  function openLockedInfo() {
    setShowBottomSheet(true)
  }

  function hideLockedInfo() {
    setShowBottomSheet(false)
  }

  // Render Methods
  // ------------------------------------------------------------------------- /
  const list = visibleContacts.map((contact, index) => {
    const {
      givenName,
      familyName,
      id,
      hospitalId,
      healthblocksId,
      kinshipId,
      primaryPhoneNumber,
      additionalPhoneNumber,
      email,
      isMandatee,
      isMandator,
    } = contact

    const mandateAllowsEdit = !isMandatee || String(actorMember?.id) === healthblocksId

    // Ignore "000000" as a hospitalId
    const isEditable = isNil(hospitalId) || isEmpty(hospitalId?.replaceAll('0', '').trim())

    return (
      <ListItem key={id ?? healthblocksId} index={index + 1}>
        <FlexColumn gap={theme.UI.Spacing.Space2}>
          {index > 0 ? (
            <IconButton
              icon={['fal', 'arrow-up-to-line']}
              variant='Tertiary'
              size='small'
              onClick={() => handleReorder(index, index - 1)}
            />
          ) : null}

          {index < visibleContacts.length - 1 ? (
            <IconButton
              icon={['fal', 'arrow-down-to-line']}
              variant='Tertiary'
              size='small'
              onClick={() => handleReorder(index, index + 1)}
            />
          ) : null}
        </FlexColumn>

        <AdditionalContactCard>
          <AdditionalContactCardContent>
            <FlexRow alignItems='center' justifyContent='space-between'>
              <Span type='BodyMediumBold' style={{ paddingRight: theme.UI.Spacing.Space6 }}>
                {givenName ? `${givenName} ` : ''}
                {familyName}
              </Span>

              <FontAwesomeIcon
                icon={isEditable && mandateAllowsEdit ? ['fal', 'trash-can'] : ['fal', 'lock']}
                onClick={
                  isEditable && mandateAllowsEdit
                    ? () => {
                        mixpanel.track('contact_delete')
                        onRemoveContact(healthblocksId)
                      }
                    : undefined
                }
              />
            </FlexRow>
            {isMandator ? (
              <Span type='BodySmallRegular' color={theme.Colors.Status.Warning}>
                {t({
                  id: 'mandator.message',
                  message: 'Je hebt een volmacht voor deze persoon',
                })}
              </Span>
            ) : null}
            {isMandatee ? (
              <Span type='BodySmallRegular' color={theme.Colors.Status.Warning}>
                {t({
                  id: 'mandatee.message',
                  message: 'Deze persoon heeft een volmacht op jou',
                })}
              </Span>
            ) : null}
            <Span type='BodyMediumRegular' style={{ lineBreak: 'anywhere' }}>
              {kinshipTypes?.find((value) => value.value === kinshipId)?.label}
            </Span>
            {primaryPhoneNumber ? (
              <Span type='BodyMediumRegular' style={{ lineBreak: 'anywhere' }}>
                {formatPhoneNumber(primaryPhoneNumber)}
              </Span>
            ) : null}
            {additionalPhoneNumber ? (
              <Span type='BodyMediumRegular' style={{ lineBreak: 'anywhere' }}>
                {formatPhoneNumber(additionalPhoneNumber)}
              </Span>
            ) : null}
            {email ? (
              <Span type='BodyMediumRegular' style={{ lineBreak: 'anywhere' }}>
                {email}
              </Span>
            ) : null}
          </AdditionalContactCardContent>
          <Line color={theme.Colors.Neutrals.Light} />
          <AdditionalContactCardContent>
            {isEditable ? (
              <AdditionalContactCardAction
                onClick={() => {
                  mixpanel.track('contact_edit_start')
                  onEditContact(contact)
                }}
              >
                {t({
                  id: 'add-contacts.button.edit',
                  message: 'Aanpassen',
                })}
              </AdditionalContactCardAction>
            ) : (
              <Span type='BodySmallRegular'>
                {t({
                  id: 'non-editable-contact',
                  message: 'Deze gegevens kunnen enkel gewijzigd worden door je contactpersoon zelf.',
                })}
              </Span>
            )}
          </AdditionalContactCardContent>
        </AdditionalContactCard>
      </ListItem>
    )
  })

  return (
    <FlexColumn style={{ minHeight: 0 }}>
      <Margin mb={theme.UI.Spacing.Space6}>
        <Flex flexDirection='column'>{list}</Flex>
      </Margin>

      {contacts.some(
        (contact) => !isNil(contact.hospitalId) && !isEmpty(contact.hospitalId?.replaceAll('0', '').trim()),
      ) ? (
        <FlexColumn
          style={{
            position: 'relative',
            padding: `${theme.UI.SpacingPx.Space6} ${theme.UI.SpacingPx.Space5}`,
            borderTop: `1px solid ${theme.Colors.Neutrals.Lighter}`,
          }}
          gap={theme.UI.Spacing.Space2}
        >
          <Flex alignItems='center' gap={theme.UI.Spacing.Space2}>
            <FontAwesomeIcon icon={['fal', 'lock']} fontSize={16} />
            <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darkest}>
              {t({
                id: 'add-contacts.locked.title',
                message: 'Vergrendelde personen',
              })}
            </Span>
          </Flex>

          <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest}>
            {t({
              id: 'add-contacts.locked.description',
              message: 'Deze gegevens kunnen enkel gewijzigd worden door je contactpersoon zelf.',
            })}
          </Span>

          <Flex
            justifyContent='center'
            alignItems='center'
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: `1px solid ${theme.Colors.Neutrals.Light}`,
            }}
            onClick={openLockedInfo}
          >
            <FontAwesomeIcon icon={['fal', 'chevron-up']} fontSize={24} />
          </Flex>
        </FlexColumn>
      ) : null}

      <Padding pt={theme.UI.Spacing.Space3} pb={theme.UI.Spacing.Space10}>
        <Button
          variant='Secondary'
          prefixIcon={['fal', 'plus']}
          onClick={() => {
            mixpanel.track('contact_creation_start')
            onAddContact()
          }}
        >
          {t({
            id: 'add-contacts.button.add',
            message: 'Contactpersoon toevoegen',
          })}
        </Button>
      </Padding>

      <BottomSheet steps={[40, 100]} visible={showBottomSheet} onClose={hideLockedInfo}>
        <Padding px={theme.UI.Spacing.Space4}>
          <FlexColumn gap={theme.UI.Spacing.Space5}>
            <H3 noMargin>
              {t({
                id: 'add-contacts.bottomsheet.title',
                message: 'Vergrendelde personen',
              })}
            </H3>
            <FlexColumn gap={theme.UI.Spacing.Space3}>
              <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
                {t({
                  id: 'add-contacts.bottomsheet.description',
                  message: 'Deze gegevens kunnen enkel gewijzigd worden door je contactpersoon zelf.',
                })}
              </Span>
              <FlexColumn>
                <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darker}>
                  {t({
                    id: 'add-contacts.bottomsheet.digital',
                    message: 'Digitaal:',
                  })}
                </Span>
                <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
                  {t({
                    id: 'add-contacts.bottomsheet.digital-description',
                    message: 'Via de eigen persoonlijke account van je contactpersoon in MijnZiekenhuis',
                  })}
                </Span>
              </FlexColumn>
              <FlexColumn>
                <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darker}>
                  {t({
                    id: 'add-contacts.bottomsheet.local',
                    message: 'Ter plaatse:',
                  })}
                </Span>
                <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
                  {t({
                    id: 'add-contacts.bottomsheet.local-description',
                    message: 'Via de infobalie of het meldpunt van je zorgverlener.',
                  })}
                </Span>
              </FlexColumn>
            </FlexColumn>
            <Button variant='Secondary' onClick={hideLockedInfo}>
              {t({
                id: 'add-contacts.bottomsheet.close',
                message: 'Sluit venster',
              })}
            </Button>
          </FlexColumn>
        </Padding>
      </BottomSheet>
    </FlexColumn>
  )
}
